/** Devigging methods this module supports, in the order devig() checks them. */
export const DEVIG_METHODS = ['multiplicative', 'additive', 'power', 'shin'];

/**
 * THE THREE A LEG'S EDGE HAS TO SURVIVE (engine review #3).
 *
 * Stripping a bookmaker's margin is a modelling choice, not a measurement, and
 * the three methods here are three different stories about where the margin
 * sits. On a short price they agree to within a point or two. On the tail of a
 * fourteen-runner field they do not agree at all: multiplicative spreads the
 * round proportionally and leaves a long shot its share of it, Shin assumes the
 * extra margin is protection against insiders and takes most of it off the long
 * shot, and power sits between them. A $26 runner can be "fair at $21" or "fair
 * at $34" depending on which one is asked, and both are defensible.
 *
 * So a leg is only called active if it clears the edge and EV floors under
 * EVERY one of them (`runStrategy`). A finding that exists under one story and
 * not another is not a finding, it is a choice of arithmetic — and the choices
 * disagree most exactly where a fair price is least trustworthy, which is the
 * long prices a growth ranking is drawn to.
 *
 * `additive` is deliberately not one of them: it is in `DEVIG_METHODS` for
 * completeness and no strategy in the app uses it.
 */
export const CERTAINTY_METHODS = ['multiplicative', 'power', 'shin'];

/**
 * How far the three have to disagree before a screen prints a RANGE instead of
 * a fair price. Ten per cent of the middle: below that the spread is smaller
 * than the rounding on the price beside it, and "fair 2.90 to 2.95" is two
 * numbers where one would do.
 */
export const FAIR_SPREAD_SHOWN = 0.10;

/**
 * The band of fair probabilities the three methods give, and how wide it is
 * relative to its own middle: `{ low, high, spread }`, or null where fewer than
 * two of them answered.
 *
 * `spread` is (high − low) / mid rather than (high − low), because a two-point
 * disagreement about a 4% chance is a different animal from a two-point
 * disagreement about a 60% one.
 */
export function fairBand(values) {
  const finite = (values ?? []).filter((v) => Number.isFinite(v) && v > 0);
  if (finite.length < 2) return null;
  const low = Math.min(...finite);
  const high = Math.max(...finite);
  const mid = (low + high) / 2;
  return { low, high, spread: mid > 0 ? (high - low) / mid : 0 };
}

/**
 * Naive implied probabilities (1/price), unnormalised — their sum is
 * 1 + overround. Throws RangeError if any price is not finite or <= 1.
 */
export function impliedProbs(prices) {
  return prices.map((price) => {
    if (!Number.isFinite(price) || price <= 1) {
      throw new RangeError(`price must be finite and > 1, got ${price}`);
    }
    return 1 / price;
  });
}

/**
 * Bisect k in [0.5, 3] so that Sum(q_i^k) = 1. q_i^k is strictly decreasing in
 * k for 0 < q_i < 1, so f(k) = Sum(q_i^k) - 1 is monotonic and bisection converges.
 */
export function powerK(q) {
  const f = (k) => q.reduce((sum, qi) => sum + Math.pow(qi, k), 0) - 1;
  let lo = 0.5;
  let hi = 3;
  let flo = f(lo);
  let mid = lo;
  for (let i = 0; i < 100; i++) {
    mid = (lo + hi) / 2;
    const fMid = f(mid);
    if (Math.abs(fMid) < 1e-9) return mid;
    if ((flo < 0) === (fMid < 0)) {
      lo = mid;
      flo = fMid;
    } else {
      hi = mid;
    }
  }
  return mid;
}

function shinP(qi, z, sumQ) {
  return (Math.sqrt(z * z + (4 * (1 - z) * qi * qi) / sumQ) - z) / (2 * (1 - z));
}

/** Bisect z in [0, 0.2) so that Sum(p_i(z)) = 1 (Shin's insider-trading model). */
export function shinZ(q) {
  const sumQ = q.reduce((sum, qi) => sum + qi, 0);
  const f = (z) => q.reduce((sum, qi) => sum + shinP(qi, z, sumQ), 0) - 1;
  let lo = 0;
  let hi = 0.2;
  let flo = f(lo);
  let mid = lo;
  for (let i = 0; i < 100; i++) {
    mid = (lo + hi) / 2;
    const fMid = f(mid);
    if (Math.abs(fMid) < 1e-9) return mid;
    if ((flo < 0) === (fMid < 0)) {
      lo = mid;
      flo = fMid;
    } else {
      hi = mid;
    }
  }
  return mid;
}

/**
 * Remove the bookmaker's overround from a set of prices, by one of
 * DEVIG_METHODS. Returns the fair probabilities plus the overround measured
 * and (for power/shin) the fitted parameter.
 */
export function devig(prices, method = 'power') {
  if (!DEVIG_METHODS.includes(method)) {
    throw new TypeError(`unknown devig method: ${method}`);
  }
  const q = impliedProbs(prices);
  const sumQ = q.reduce((sum, qi) => sum + qi, 0);
  const overround = sumQ - 1;

  if (method === 'multiplicative') {
    return { probs: q.map((qi) => qi / sumQ), overround, method, param: null };
  }

  if (method === 'additive') {
    const adj = (sumQ - 1) / q.length;
    const clamped = q.map((qi) => Math.max(qi - adj, 1e-6));
    const clampedSum = clamped.reduce((sum, x) => sum + x, 0);
    return { probs: clamped.map((x) => x / clampedSum), overround, method, param: null };
  }

  if (method === 'power') {
    const k = powerK(q);
    const powered = q.map((qi) => Math.pow(qi, k));
    const poweredSum = powered.reduce((sum, x) => sum + x, 0);
    return { probs: powered.map((x) => x / poweredSum), overround, method, param: k };
  }

  // shin — with no overround there's nothing to remove, so fall back rather
  // than bisect toward a meaningless negative-z root.
  if (sumQ <= 1) {
    return { probs: q.map((qi) => qi / sumQ), overround, method, param: null };
  }
  const z = shinZ(q);
  return { probs: q.map((qi) => shinP(qi, z, sumQ)), overround, method, param: z };
}
